import './dyf-feature-article-overview-view.scss';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ServerContext from '../../contexts/serverContext';
import { useDyfFeatureGuideBreadcrumbsContent } from '../dyf-landing-view/hooks/use-dyf-feature-guide-breadcrumbs-content';
import { DyfFeatureGuideBreadCrumbs } from '../../components/sections/dyf-landing/dyf-feature-guide-breadcrumbs/dyf-feature-guide-breadcrumbs';
import { useFeatureGuideContent } from '../dyf-landing-view/hooks/use-dyf-feature-guide-content';
import DyfFeatureRibbon from '../../components/sections/onboarding-global-components/onboarding-feature-ribbon/dyf-feature-ribbon';
import { DyfMoreWaysToLearn } from '../../components/sections/dyf-landing/feature-guide-more-ways-to-learn/dyf-more-ways-to-learn';
import { VehicleAttributes } from '../../models/vehicle-attributes';
import { useAnalytics } from '../../hooks/use-analytics';
import { FeatureRibbonDisclaimerFooter } from '../../components/sections/onboarding-global-components/onboarding-feature-ribbon/feature-ribbon-disclaimer-footer/feature-ribbon-disclaimer-footer';
import {
    AuthenticationState,
    useAuthentication,
} from '../../hooks/use-authentication';
import { StringUtil } from '../../components/utils/string-util/string-util';
import { useServerData } from '../../hooks/use-server-data';
import AstuteService from '../../support/services/astute-service/astute-service';
import AuthenticationService from '../../services/authentication-service/authentication-service';

import { useAuthenticatedArticles } from '../../hooks/dyf/use-authenticated-articles/use-authenticated-articles';
import { getYmmDetails } from '../dyf-landing-view/util/get-ymm-details';
import { VehicleYmm } from '../../models/vehicle-ymm';
import { useDyfQueryParams } from '../../hooks/dyf/use-dyf-query-params/use-dyf-query-params';
import { OnboardingLoader } from '../dyf-landing-view/onboarding-loader/onboarding-loader';
import { FMVehicleSelector } from '../../components/common/fm-vehicle-selector/fm-vehicle-selector';

export type ArticleOverviewViewProps = RouteComponentProps<any, any, any> & {
    flow: 'ymm' | 'vin';
};
export const DyfFeatureArticleOverview = (props: ArticleOverviewViewProps) => {
    const {
        queryModel = '',
        queryVin = '',
        queryYear = '',
        queryTitle = '',
        queryCategory = '',
    } = useDyfQueryParams();
    const [contentLoaded, setContentLoaded] = useState<boolean>(false);

    const [vehicleYmmGroup, setVehicleYmmGroup] = useState<VehicleYmm | null>(
        null
    );

    const [authState] = useAuthentication();

    const isAuthenticated = useMemo(() => {
        return authState === AuthenticationState.Authenticated;
    }, [authState]);

    const {
        brand,
        currentRegionCode,
        currentLanguageRegionCode,
        current3LetterCountryCode,
        root,
    } = useContext(ServerContext);

    const featureGuideContent = useFeatureGuideContent();
    const history = useHistory();
    const dyfOverviewArticleBreadcrumbs = useDyfFeatureGuideBreadcrumbsContent();
    const [fireEvents] = useAnalytics();

    const {
        loading: authenticatedArticlesLoading,
        publicCategories,
        dyfEligibleModel,
    } = useAuthenticatedArticles();

    const topicContent = useServerData<{
        response: string;
        categoryName: string;
        topicId: number | null;
    } | null>('dyfArticleOverview', async () => {
        let content = '';
        let foundCategory = '';
        const astute = new AstuteService();
        const topicId = await astute.getTopicIdFromSeoTitle(
            queryTitle,
            currentLanguageRegionCode,
            brand
        );
        if (topicId) {
            content = await astute
                .getTopicContentById(
                    topicId,
                    currentLanguageRegionCode,
                    root,
                    brand
                )
                .then(res => res?.response || '');
            foundCategory = await astute
                .getCategoryNameFromTopicId(
                    topicId,
                    currentLanguageRegionCode,
                    brand
                )
                .then(res => res?.category || '');
        }
        return {
            response: content,
            categoryName: foundCategory,
            topicId: topicId,
        };
    })[0];

    useEffect(() => {
        if (topicContent) {
            setContentLoaded(true);
        }
    }, [topicContent]);

    useEffect(() => {
        getYmmDetails({
            flow: props.flow,
            queryVin,
            queryModel,
            queryYear,
            currentLanguageRegionCode,
            current3LetterCountryCode,
            currentRegionCode,
            brand,
        }).then(res => setVehicleYmmGroup(res ?? null));
    }, []);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    }, []);

    const currentSearchVehicle = useMemo(() => {
        if (vehicleYmmGroup)
            return {
                year: vehicleYmmGroup?.year,
                make: vehicleYmmGroup?.make,
                model: vehicleYmmGroup?.model,
                seoKey: vehicleYmmGroup?.seoKey,
                vin: queryVin,
            };
        else return null;
    }, [vehicleYmmGroup]);

    const isEligibleTowingCalculator = useMemo(() => {
        return dyfEligibleModel?.eligibleTowingCalculator || false;
    }, [dyfEligibleModel]);

    const newPaths = ['/vehicle/vin/', '/discover-your-ford', '/vehicle/'];

    const vehicleAttributes: VehicleAttributes = {
        year: vehicleYmmGroup?.year || 0,
        make: vehicleYmmGroup?.make || '',
        model: vehicleYmmGroup?.marketName || '',
    };

    const vehicleMake = currentSearchVehicle?.make || vehicleAttributes?.make;

    useEffect(() => {
        const componentName = {
            componentName: 'Feature guide article overview',
            ymm: currentSearchVehicle || vehicleAttributes,
        };
        if (vehicleMake && props.flow === 'vin') {
            fireEvents(
                ['dyf-feature-guide-article-overview-page-vin'],
                undefined,
                componentName
            );
        } else if (vehicleMake && props.flow === 'ymm') {
            fireEvents(
                ['dyf-feature-guide-article-overview-page-ymm'],
                undefined,
                componentName
            );
        }
    }, [vehicleMake]);

    const changeFeatureGuide = (match: VehicleAttributes) => {
        const newVinPath = newPaths
            ? `${newPaths[0]}${match.vin}${newPaths[1]}`
            : undefined;
        if (newVinPath) {
            history.replace({
                pathname: newVinPath,
            });
        }
    };
    const handleOnChange = (
        title: string,
        index: number,
        value: string,
        match: VehicleAttributes
    ) => {
        changeFeatureGuide(match);
    };

    const loginAndReturnToPage = () => {
        const authService = new AuthenticationService();
        authService.login();
    };

    useEffect(() => {
        if (
            !authenticatedArticlesLoading &&
            authState == AuthenticationState.Unauthenticated
        ) {
            if (
                !publicCategories
                    .flatMap(c => c.queryName)
                    .includes(queryCategory)
            ) {
                loginAndReturnToPage();
            }
        }
    }, [authenticatedArticlesLoading, authState, loginAndReturnToPage]);

    if (!contentLoaded) {
        return (
            <>
                <OnboardingLoader />
            </>
        );
    }

    return (
        <div className="feature-article-overview-wrapper">
            {dyfOverviewArticleBreadcrumbs && ( //TODO: refactor after YMM API integration
                <DyfFeatureGuideBreadCrumbs
                    origin={'overview'}
                    dyfFeatureGuideBreadcrumbs={dyfOverviewArticleBreadcrumbs}
                    vinDetails={
                        props.flow == 'vin'
                            ? {
                                  vehicleName: vehicleYmmGroup?.model || '',
                                  vin: queryVin || '',
                              }
                            : null
                    }
                    ymmDetails={
                        props.flow == 'ymm'
                            ? { modelName: vehicleYmmGroup?.model || '' }
                            : null
                    }
                />
            )}
            {currentSearchVehicle && isAuthenticated && (
                <FMVehicleSelector
                    vehicleAttributes={currentSearchVehicle}
                    onChange={handleOnChange}
                    page={'DYF Feature Guide'}
                    disableDashboardRedirect={true}
                />
            )}
            {dyfEligibleModel?.eligibleFeatureGuide &&
                featureGuideContent &&
                vehicleYmmGroup && (
                    <DyfFeatureRibbon
                        vin={queryVin}
                        featureGuideContent={featureGuideContent}
                        vehicleType={dyfEligibleModel.vehicleType}
                        eligibleFeatureGuide={
                            dyfEligibleModel.eligibleFeatureGuide
                        }
                        eligibleTowingCalculator={isEligibleTowingCalculator}
                        vehicleYmmGroup={vehicleYmmGroup}
                    />
                )}
            {topicContent?.data?.response && (
                <div data-testId="article-content">
                    <div className="feature-article-overview-title">
                        {featureGuideContent?.longDescriptionTitle?.replace(
                            '[category]',
                            topicContent.data?.categoryName ||
                                StringUtil.removeHyphen(queryCategory) ||
                                ''
                        )}
                    </div>
                    <div
                        className="long-description-wrapper"
                        dangerouslySetInnerHTML={{
                            __html: topicContent.data?.response || '',
                        }}
                    />
                </div>
            )}

            {isAuthenticated && vehicleYmmGroup && props.flow == 'vin' && (
                <div className="more-ways-to-learn-component">
                    <DyfMoreWaysToLearn
                        vehicleYear={`${vehicleYmmGroup.year}`}
                        baseRoot={
                            root ? root.substring(0, root.length - 1) : ''
                        }
                        renderView="article-overview"
                        vehicleYmmGroup={vehicleYmmGroup}
                        currentLanguageRegionCode={currentLanguageRegionCode}
                    />
                </div>
            )}
            {dyfEligibleModel && props.flow == 'vin' && (
                <FeatureRibbonDisclaimerFooter
                    eligibleTowingCalculator={
                        dyfEligibleModel?.eligibleTowingCalculator || false
                    }
                    vin={queryVin}
                    vehicleType={dyfEligibleModel?.vehicleType}
                />
            )}
        </div>
    );
};

export default DyfFeatureArticleOverview;
